/**
 * React Query Helpers
 *
 * Provides utilities for creating React Query options with automatic cache key generation.
 */

import { useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

/**
 * Simplified query options type - no need to write Omit every time
 * Omits queryKey, queryFn, and initialData as they are managed by useApiQuery
 */
export type QueryOptions<TData, TError = Error> = Omit<
  UseQueryOptions<TData, TError>,
  'queryKey' | 'queryFn' | 'initialData'
>;

/**
 * Simplified mutation options type - no need to write Omit every time
 */
export type MutationOptions<TData, TVariables = void, TError = Error> = Omit<
  UseMutationOptions<TData, TError, TVariables>,
  'mutationFn'
>;

/**
 * Callback options for API query hooks
 *
 * Provides onSuccess/onError callbacks for query hooks.
 *
 * @template TResponse - The API response type
 * @template TError - The error type (default: Error)
 */
export interface GeneralApiQueryOption<TResponse, TError = Error> {
  /**
   * Callback fired when query succeeds
   * @param data - The resolved response data
   */
  onSuccess?: (data: TResponse) => void;
  /**
   * Callback fired when query fails
   * @param error - The error that occurred
   */
  onError?: (error: TError) => void;
}

/**
 * Base options for generated API query hooks
 *
 * Combines query options and callbacks into a single interface.
 * Generated UseXxxOptions should extend this interface, specifying the response type once.
 *
 * @template TResponse - The API response type
 * @template TError - The error type (default: Error)
 *
 * @example
 * // Generated hook options - specify response type only once
 * export interface UseCoinsOptions extends BaseQueryHookOptions<Coins> {}
 *
 * export interface UseCurrenciesOptions extends BaseQueryHookOptions<Currency[]> {
 *   body?: Partial<GeneralRequestBody>;
 * }
 *
 * // Usage
 * const { data } = useCoins({
 *   onSuccess: (data) => console.log('Loaded:', data),
 *   onError: (error) => console.error('Failed:', error),
 * });
 */
export interface BaseQueryHookOptions<TResponse, TError = Error>
  extends GeneralApiQueryOption<TResponse, TError> {
  /**
   * React Query options
   */
  queryOptions?: QueryOptions<TResponse, TError>;
}

/**
 * Callback options for API mutation hooks
 *
 * Provides onSuccess/onError callbacks for mutation hooks.
 *
 * @template TResponse - The API response type
 * @template TRequest - The request body type (default: void)
 * @template TError - The error type (default: Error)
 */
export interface GeneralApiMutationOption<
  TResponse,
  TRequest = void,
  TError = Error,
> {
  /**
   * Callback fired when mutation succeeds
   * @param data - The resolved response data
   * @param variables - The request variables passed to mutate
   */
  onSuccess?: (data: TResponse, variables: TRequest) => void;
  /**
   * Callback fired when mutation fails
   * @param error - The error that occurred
   * @param variables - The request variables passed to mutate
   */
  onError?: (error: TError, variables: TRequest) => void;
}

/**
 * Base options for generated API mutation hooks
 *
 * Combines React Query mutation options with simplified callbacks.
 * The onSuccess/onError callbacks have simpler signatures than React Query's native ones.
 *
 * @template TResponse - The API response type
 * @template TRequest - The request body type (default: void)
 * @template TError - The error type (default: Error)
 *
 * @example
 * // Generated hook options - specify types once
 * export interface UseCreateApiKeyOptions
 *   extends BaseMutationHookOptions<ApiKey, CreateApiKeyBody> {}
 *
 * // Usage
 * const { mutate } = useCreateApiKey({
 *   onSuccess: (data) => console.log('Created:', data),
 *   onError: (error) => console.error('Failed:', error),
 * });
 */
export interface BaseMutationHookOptions<
  TResponse,
  TRequest = void,
  TError = Error,
> extends MutationOptions<TResponse, TRequest, TError> {}

/**
 * Internal options for API query (used by createQueryOptions)
 */
interface ApiQueryOptions<TData, TError, TArgs = never>
  extends Omit<
    UseQueryOptions<TData, TError>,
    'queryKey' | 'queryFn' | 'initialData'
  > {
  /** Base cache key (auto-generated from queryFn.name if not provided) */
  cacheKey?: string;
  /** Query parameters (for GET requests - appended to URL as query string) */
  params?: TArgs;
  /** Request body (for POST/PUT/DELETE requests - sent as request payload) */
  body?: TArgs;
  /**
   * Function to fetch data
   * - Can be a no-arg function: () => Promise<TData>
   * - Or a function accepting args: (args: TArgs) => Promise<TData>
   * - Args can be params (GET) or body (POST/PUT/DELETE)
   */
  queryFn: ((args: TArgs) => Promise<TData>) | (() => Promise<TData>);
}

/**
 * Options for generated hooks (two-parameter form with nested queryOptions)
 *
 * Extends GeneralApiQueryOption to inherit onSuccess/onError callbacks.
 */
export interface HookOptions<TData, TError, TArgs = never>
  extends GeneralApiQueryOption<TData, TError> {
  /** Base cache key (auto-generated from queryFn.name if not provided) */
  cacheKey?: string;
  /** Query parameters (for GET requests - appended to URL as query string) */
  params?: TArgs;
  /** Request body (for POST/PUT/DELETE requests - sent as request payload) */
  body?: TArgs;
  /** React Query options (nested) */
  queryOptions?: QueryOptions<TData, TError>;
}

/**
 * Helper to generate cache keys from a base key and optional parameters
 *
 * @param cacheKey - Base cache key (e.g., 'tickers', 'sparklines')
 * @param params - Optional parameters to include in the cache key
 * @returns Array suitable for React Query queryKey
 *
 * @example
 * getCacheKey('tickers') // ['tickers']
 * getCacheKey('sparklines', { instrumentType: 'CCY_PAIR' }) // ['sparklines', { instrumentType: 'CCY_PAIR' }]
 */
export function getCacheKey<TParams = never>(
  cacheKey?: string,
  params?: TParams,
): readonly unknown[] {
  if (!cacheKey) return [];

  if (params === undefined || params === null) {
    return [cacheKey] as const;
  }

  // Filter out undefined/null values from params
  const cleanParams: Record<string, any> = {};
  for (const [key, value] of Object.entries(params as any)) {
    if (value !== undefined && value !== null) {
      cleanParams[key] = value;
    }
  }

  return Object.keys(cleanParams).length === 0
    ? ([cacheKey] as const)
    : ([cacheKey, cleanParams] as const);
}

/**
 * Internal helper to create React Query options with automatic cache key generation
 */
function createQueryOptions<TData, TError = Error, TArgs = never>(
  options: ApiQueryOptions<TData, TError, TArgs>,
): UseQueryOptions<TData, TError> {
  const { cacheKey, params, body, queryFn, ...restOptions } = options;

  // Automatically generate queryKey from cacheKey (if provided) or queryFn.name
  // If cacheKey is not provided, try to extract from queryFn.name
  const baseCacheKey = cacheKey || queryFn.name || '';

  // Use body if provided (POST/PUT/DELETE), otherwise use params (GET)
  const args = body ?? params;
  const queryKey = getCacheKey(baseCacheKey, args);

  // Wrap queryFn to prevent React Query's QueryFunctionContext from being passed to the API function
  // React Query calls queryFn with QueryFunctionContext { queryKey, signal, meta, ... }
  // We need to ignore this context and only pass our args (if any)
  const actualQueryFn =
    args !== undefined
      ? () => (queryFn as (args: TArgs) => Promise<TData>)(args)
      : () => (queryFn as () => Promise<TData>)();

  return {
    queryKey,
    queryFn: actualQueryFn,
    ...restOptions,
  } as UseQueryOptions<TData, TError>;
}

/**
 * React Query hook with automatic cache key generation
 *
 * Simplified API for data fetching - no need to import @tanstack/react-query.
 *
 * Cache Key Generation:
 * - If `cacheKey` is provided, uses `cacheKey + args`
 * - If `cacheKey` is not provided, uses `queryFn.name + args`
 * - Args can be `params` (GET) or `body` (POST/PUT/DELETE)
 *
 * @param queryFn - Query function to fetch data
 * @param options - Hook options (optional)
 * @returns React Query result
 *
 * @example
 * // Basic usage (no arguments)
 * export function useFundingRates(options?: UseFundingRatesOptions) {
 *   return useApiQuery(fetchFundingRates, options);
 * }
 *
 * @example
 * // With query options
 * export function useCurrencies(options?: UseCurrenciesOptions) {
 *   return useApiQuery(fetchCurrencies, {
 *     queryOptions: {
 *       staleTime: 5 * 60 * 1000,
 *       gcTime: 10 * 60 * 1000,
 *       structuralSharing: true,
 *     },
 *     ...options,
 *   });
 * }
 */
// Overload 1: With args
export function useApiQuery<TData, TError = Error, TArgs = never>(
  queryFn: (args: TArgs) => Promise<TData>,
  options?: HookOptions<TData, TError, TArgs>,
): ReturnType<typeof useQuery<TData, TError>>;

// Overload 2: Without args
export function useApiQuery<TData, TError = Error>(
  queryFn: () => Promise<TData>,
  options?: HookOptions<TData, TError, never>,
): ReturnType<typeof useQuery<TData, TError>>;

// Implementation
export function useApiQuery<TData, TError = Error, TArgs = never>(
  queryFn: ((args: TArgs) => Promise<TData>) | (() => Promise<TData>),
  options?: HookOptions<TData, TError, TArgs>,
): ReturnType<typeof useQuery<TData, TError>> {
  const { cacheKey, params, body, queryOptions, onSuccess, onError } =
    options || {};

  // Flatten the nested queryOptions
  const apiQueryOptions: ApiQueryOptions<TData, TError, TArgs> = {
    queryFn,
    cacheKey,
    params,
    body,
    ...queryOptions,
  };

  const queryResult = useQuery(createQueryOptions(apiQueryOptions));

  // Track previous states to detect transitions
  const prevIsSuccessRef = useRef(false);
  const prevIsErrorRef = useRef(false);

  // Handle onSuccess callback
  // React Query v5 removed onSuccess from useQuery options, so we use useEffect
  useEffect(() => {
    if (queryResult.isSuccess && !prevIsSuccessRef.current && onSuccess) {
      onSuccess(queryResult.data);
    }
    prevIsSuccessRef.current = queryResult.isSuccess;
  }, [queryResult.isSuccess, queryResult.data, onSuccess]);

  // Handle onError callback
  // React Query v5 removed onError from useQuery options, so we use useEffect
  useEffect(() => {
    if (queryResult.isError && !prevIsErrorRef.current && onError) {
      onError(queryResult.error);
    }
    prevIsErrorRef.current = queryResult.isError;
  }, [queryResult.isError, queryResult.error, onError]);

  return queryResult;
}

/**
 * React Query mutation hook for API mutations
 *
 * Simplified API for mutations - consistent with useApiQuery(fn, options) pattern.
 * Platform-independent - does not include toast notifications.
 *
 * @param mutationFn - Function to perform the mutation
 * @param options - Optional mutation configuration (onSuccess, onError, etc.)
 * @returns React Query mutation result
 *
 * @example
 * // Basic usage - just return the hook directly
 * export function useCreateApiKey(options: UseCreateApiKeyOptions = {}) {
 *   return useApiMutation(createApiKey, options);
 * }
 *
 * @example
 * // With callbacks
 * const { mutate, isPending } = useCreateApiKey({
 *   onSuccess: (data) => console.log('Created:', data),
 *   onError: (error) => console.error('Failed:', error),
 * });
 */
export function useApiMutation<TData, TVariables = void, TError = Error>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: MutationOptions<TData, TVariables, TError>,
) {
  return useMutation({
    mutationFn,
    ...options,
  });
}
